/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { distinctUntilChanged$, filter$, map$, switchMap$ } from '@gh/rx/operators';
import { combineLatest, Observable, Subscription } from 'rxjs';
import {
  getHpeCostCenterId,
  getHpeItemId,
  HpeItem,
  HpeItemType,
  isHpeFreeItem,
  isHpeItemFromComponent,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  ensureHpeSalesTaxDivider,
  HpeDetailsState,
  HpeFocusedTable,
  queryHpeCostCenterItems,
  queryHpeEditable,
  queryHpeFocusedCell,
  queryHpeSelectedCostCenter,
  queryHpeSelectedCostCenterId,
  queryHpeSelectedItemId,
} from '../hpe-details.state';
import { HpeDetailsCcItemGridDefinition } from './hpe-details-cc-item-grid-definition.service';

@Component({
  selector: 'gh-hpe-details-cc-item-list',
  template: require('./hpe-details-cc-item-list.component.html'),
  styles: [require('./hpe-details-cc-item-list.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HpeDetailsCcItemGridDefinition],
})
export class HpeDetailsCcItemListComponent implements OnInit, OnDestroy {
  total$: Observable<number>;
  cost$: Observable<number>;
  combineCosts$: Observable<boolean>;
  items$: Observable<HpeItem[]>;
  selectedItemId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  editable: boolean;

  private costCenterId: ID;
  private costCenterIdSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              public gridDefinition: HpeDetailsCcItemGridDefinition,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {

  }

  ngOnInit(): void {
    const { hpeItemGridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(hpeItemGridLayout);

    const { state } = this;
    const costCenterId$ = state.select(queryHpeSelectedCostCenterId).pipe(filter$(Boolean));
    const costCenter$ = state.select(queryHpeSelectedCostCenter).pipe(filter$(Boolean));

    this.items$ = costCenter$.pipe(
      switchMap$((cc: any) => state.select(queryHpeCostCenterItems, getHpeCostCenterId(cc)).pipe(
        map$(ensureHpeSalesTaxDivider))));
    this.total$ = costCenter$.pipe(map$((component: any) => component.total || 0), distinctUntilChanged$());
    this.cost$ = costCenter$.pipe(map$((component: any) => component.cost || 0), distinctUntilChanged$());
    this.combineCosts$ = combineLatest(this.cost$, this.total$).pipe(
      map$(([cost, total]) => cost === total));
    this.selectedItemId$ = state.select(queryHpeSelectedItemId);
    this.focusedCell$ = this.state.select(queryHpeFocusedCell, HpeFocusedTable.ItemList);

    this.costCenterIdSubscription = costCenterId$.subscribe((id: any) => this.costCenterId = id);
    this.editable = this.state.project(queryHpeEditable);
  }

  ngOnDestroy(): void {
    this.costCenterIdSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.costCenterItemAdd(this.costCenterId);
  }

  onhpeCloneItems(item: HpeItem): void {
    this.commands.costCenterItemClone(item);
  }

  onDelete(item: HpeItem): void {
    this.commands.costCenterItemRemove(item);
  }

  canBeDeleted(item: HpeItem): boolean {
    return item.type === HpeItemType.Item;
  }

  onSelectedItemChanged(item?: HpeItem): void {
    if (item) {
      if (item.type === HpeItemType.Item || item.type === HpeItemType.Component) {
        this.commands.itemSelect(getHpeItemId(item));
      }
    } else {
      this.commands.itemSelect(void 0);
    }
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  filterCostCenterItem(item: HpeItem): boolean {
    return isHpeFreeItem(item) || isHpeItemFromComponent(item);
  }
}
